<template>
  <SAIPageWrapper>
    <CarSearchTabs />
    <SAITitle class="q-mb-lg" text-align="left">
      Select your Colours and Extras
    </SAITitle>
    <div class="row justify-center" v-if="loading">
      <SAISpinner />
    </div>
    <div class="column q-gutter-y-lg" v-else>
      <ColourAndExtrasAccessoryInclusions
        v-if="includedEquipment.length"
        :accessories="includedEquipment"
        class="row"
      />
      <SAIFormError class="q-mt-md" v-if="errors.$error && errors.$dirty" />
      <div>
        <SAIFormLabel>Exterior Colour*</SAIFormLabel>
        <ColourAndExtraSelect
          v-if="exteriorColours.length"
          :value="selectedExteriorColour"
          :options="exteriorColours"
          :disable="disable"
          placeholder="Any Colour"
          :precluded="allPrecluded"
          :all-required="allRequired"
          clearable
          :error="errors.selectedExteriorColour.$error"
          @input="$emit('update:exterior-colour', $event)"
        />
        <SAIInput
          v-else
          :value="
            selectedExteriorColour ? selectedExteriorColour.name : undefined
          "
          :disable="disable"
          placeholder="Any Colour"
          :error="errors.selectedExteriorColour.$error"
          @input="$emit('update:exterior-colour-name', $event)"
        />
      </div>
      <div>
        <SAIFormLabel>Interior Trim*</SAIFormLabel>
        <ColourAndExtraSelect
          v-if="interiorColours.length"
          :value="selectedInteriorColour"
          :options="interiorColours"
          :disable="disable || !selectedExteriorColour"
          placeholder="Any Type"
          :precluded="allPrecluded"
          :all-required="allRequired"
          clearable
          :error="errors.selectedInteriorColour.$error"
          @input="$emit('update:interior-trim', $event)"
        />
        <SAIInput
          v-else
          :value="
            selectedInteriorColour ? selectedInteriorColour.name : undefined
          "
          :disable="disable"
          placeholder="Any Type"
          :error="errors.selectedInteriorColour.$error"
          @input="$emit('update:interior-trim-name', $event)"
        />
      </div>
      <div>
        <SAIFormLabel>Optional Extras</SAIFormLabel>
        <RemovableInputLine
          class="q-mb-sm"
          v-for="(selectedEquipment, index) in selectedOptionalEquipment"
          :key="`accessory-${index}`"
          :disable="
            disable || selectedEquipment
              ? Boolean(allRequired[selectedEquipment.id])
              : false
          "
          @remove="$emit('remove:optional-equipment', index)"
        >
          <ColourAndExtraSelect
            :disable="disable"
            :precluded="allPrecluded"
            :all-required="allRequired"
            :value="selectedEquipment"
            :options="optionalEquipment"
            :clearable="false"
            @input="
              $emit('update:existing-optional-equipment', {
                index,
                update: $event
              })
            "
          />
        </RemovableInputLine>
        <ColourAndExtraSelect
          v-for="(choice, index) in selectedChoices"
          :key="`choice-${index}`"
          :disable="true"
          :clearable="false"
          :value="choice"
        />
        <RemovableInputLine
          class="q-mb-sm"
          v-for="(equipment, index) in adhocEquipment"
          :key="`adhoc-${index}`"
          :disable="disable"
          :value="equipment"
          :error="errors.customerOptions.adhocEquipment.$each[index].$error"
          @input="$emit('update:adhoc-equipment', { value: $event, index })"
          @remove="$emit('remove:adhoc-equipment', index)"
        />
        <ColourAndExtraSelect
          v-if="showAddOptionalExtra"
          class="q-mb-sm"
          :options="optionalEquipment"
          :disable="disable"
          :precluded="allPrecluded"
          :all-required="allRequired"
          placeholder="Any Accessory"
          @input="$emit('add:new-optional-equipment', $event)"
        />
        <RemovableInputLine
          v-if="showTempAdhocExtra"
          :value="tempAdhocEquipment"
          :disable="disable"
          :clearable="false"
          placeholder="Enter name of accessory"
          :error="errors.tempAdhocEquipment.$error"
          @input="$emit('update:temp-adhoc-extra', $event)"
          @remove="$emit('remove:temp-adhoc-extra')"
        />
        <div class="row justify-between q-mt-sm">
          <QBtn
            flat
            icon="eva-plus-outline"
            dense
            :disable="disable || showAddOptionalExtra"
            no-caps
            label="Standard Extra"
            :ripple="false"
            color="primary"
            @click="$emit('show:add-optional-extra')"
          />
          <QBtn
            v-if="adhocEquipment.length + 1 < maximumAdhocEquipment"
            flat
            icon="eva-plus-outline"
            dense
            :disable="disable || (showTempAdhocExtra && !tempAdhocEquipment)"
            no-caps
            label="Non Standard Extra"
            :ripple="false"
            color="primary"
            @click="$emit('add:temp-adhoc-extra')"
          />
        </div>
      </div>
      <div class="row q-mt-lg justify-center">
        <SAIBtn
          label="I'm done, get me a quote"
          no-caps
          padding="12px xl"
          :loading="saving"
          :disable="disable"
          @click="$emit('next')"
        />
      </div>
    </div>
  </SAIPageWrapper>
</template>

<script>
import {
  SAIPageWrapper,
  SAIBtn,
  SAITitle,
  SAIFormLabel,
  SAIFormError,
  SAIInput,
  SAISpinner
} from 'ecx.saigon.ui.common';
import { QBtn } from 'quasar';
import RemovableInputLine from '@/components/molecule/removable-input-line';

export default {
  name: 'CarColourAndExtras',
  props: {
    disable: Boolean,
    loading: Boolean,
    saving: Boolean,
    selectedExteriorColour: undefined,
    selectedInteriorColour: undefined,
    showAddOptionalExtra: Boolean,
    showTempAdhocExtra: Boolean,
    selectedOptionalEquipment: {
      type: Array,
      default: () => []
    },
    selectedChoices: {
      type: Array,
      default: () => []
    },
    allPrecluded: {
      type: Object,
      default: () => ({})
    },
    allRequired: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({
        selectedExteriorColour: {},
        selectedInteriorColour: {},
        customerOptions: {
          adhocEquipment: {
            $each: []
          }
        }
      })
    },
    exteriorColours: {
      type: Array,
      default: () => []
    },
    interiorColours: {
      type: Array,
      default: () => []
    },
    optionalEquipment: {
      type: Array,
      default: () => []
    },
    includedEquipment: {
      type: Array,
      default: () => []
    },
    adhocEquipment: {
      type: Array,
      default: () => []
    },
    tempAdhocEquipment: undefined,
    maximumAdhocEquipment: Number
  },
  components: {
    RemovableInputLine,
    SAIPageWrapper,
    SAITitle,
    SAIFormLabel,
    QBtn,
    SAIBtn,
    SAIFormError,
    SAIInput,
    SAISpinner,
    ColourAndExtrasAccessoryInclusions: () =>
      import('../../organism/colour-and-extras-accessory-inclusions'),
    ColourAndExtraSelect: () => import('../../atom/colour-and-extra-select'),
    CarSearchTabs: () => import('../../../widgets/car-search-tabs')
  }
};
</script>
